/**
 * Mini cart component
 * Displays cart items, quantities, pricing, discounts, promolines, and free shipping progress
 */

import clsx from 'clsx'

import { useEffect, useLayoutEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, globalState } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { cart as cartApi, Cart } from 'helpers/cart'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/athos/components/Promoline'
import { useGwp } from '@/components/hooks/useGwp'
import { GwpCartBlock, GwpGiftItem } from '@/components/custom-elements/gwp-cart-block'

const actionKeys = {
  SELECT: 'select',
  REMOVE: 'remove'
}

const QUANTITY_OPTIONS_MAX = 10

/**
 * Resolves the real inventory cap for a line item.
 *
 * Returns `null` (no limit) when inventory is untracked (`inventory_management`
 * empty) or the variant is set to keep selling when out of stock
 * (`inventory_policy === 'continue'`). Otherwise returns the tracked stock
 * level so genuine deny-policy variants stay capped at real stock.
 */
const getInventoryLimit = (item) => {
  if (!item.inventory_management) return null
  if (item.inventory_policy === 'continue') return null
  return item.inventory_quantity
}

const buildQuantityOptions = (currentQuantity, inventoryLimit) => {
  let max = QUANTITY_OPTIONS_MAX
  if (typeof inventoryLimit === 'number' && inventoryLimit > 0 && inventoryLimit < max) {
    max = inventoryLimit
  }
  if (currentQuantity > max) {
    max = currentQuantity
  }
  if (max < 1) max = 1
  return Array.from({ length: max }, (_, i) => i + 1)
}

const getUniversalSizeGender = (item) => {
  if (!item.mfield_products_universal_sizing_flag) return null
  const tags = item.tags ?? []
  if (tags.includes('Gender:Womens') && !tags.includes('Gender:Unisex')) return 'W'
  return 'M'
}

const getUniversalSizeValue = (value, genderChar) => {
  if (!genderChar || !value) return value
  const parts = value.split('/').map((part) => part.trim())
  if (parts.length < 2) return value
  return parts.find((part) => part.charAt(0).toUpperCase() === genderChar) ?? value
}

const onChangeActionValue = (key, target) => {
  return {
    [actionKeys.SELECT]: parseInt(target.value, 10),
    [actionKeys.REMOVE]: 0
  }[key]
}

/**
 * Builds the variant selector config for a line item.
 *
 * Expects `item.variants` (injected via the ajax cart Liquid template) as an
 * array of `{ id, title, available, option1, option2, option3 }`. Determines
 * which product option(s) actually vary so the dropdown can be labelled with
 * that option's name (e.g. "Size") and show each variant's option value.
 */
const getVariantSelectorConfig = (item) => {
  const variants = item.variants ?? []
  if (variants.length <= 1) return { variants: [], selectableOptions: [] }

  const optionNames = (item.options_with_values ?? []).map((option) => option.name)
  const selectableOptions = []
  optionNames.forEach((name, index) => {
    const optionValues = new Set(variants.map((variant) => variant[`option${index + 1}`]))
    if (optionValues.size > 1) {
      selectableOptions.push({ name, number: index + 1 })
    }
  })

  return { variants, selectableOptions }
}

/**
 *
 * Handles line item variant and quantity updates, plus removal.
 *
 * Renders the variant selector (when the product has more than one variant)
 * next to the quantity selector, matching the reference design.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemControls = ({ item, handleSetCartConfig, itemLoading, setItemLoading }) => {
  const [error, setError] = useState(null)
  const [optimisticQuantity, setOptimisticQuantity] = useState(null)

  useEffect(() => {
    setOptimisticQuantity(null)
  }, [item.quantity])

  const inventoryLimit = useMemo(() => getInventoryLimit(item), [
    item.inventory_management,
    item.inventory_policy,
    item.inventory_quantity
  ])

  const quantityOptions = useMemo(
    () => buildQuantityOptions(item.quantity, inventoryLimit),
    [item.quantity, inventoryLimit]
  )

  const { variants, selectableOptions } = useMemo(
    () => getVariantSelectorConfig(item),
    [item.variants, item.options_with_values]
  )

  const universalSizeGender = useMemo(
    () => getUniversalSizeGender(item),
    [item.mfield_products_universal_sizing_flag, item.tags]
  )

  const hasVariantSelector = variants.length > 1
  const singleOption = selectableOptions.length === 1 ? selectableOptions[0] : null
  const variantLabel = singleOption ? `${singleOption.name}: ` : 'Variant: '
  const getVariantOptionLabel = (variant) =>
    singleOption
      ? getUniversalSizeValue(variant[`option${singleOption.number}`], universalSizeGender)
      : variant.title
  const currentVariant = variants.find((variant) => String(variant.id) === String(item.variant_id))
  const currentVariantOptionLabel = currentVariant ? getVariantOptionLabel(currentVariant) : ''

  useEffect(() => {
    setOptimisticQuantity(null)
  }, [item.quantity])

  /**
   * Blocks only genuine over-stock increases. Removals/decreases
   * (`newQuantity <= 0` or lower) and untracked / "continue selling" variants
   * (`inventoryLimit === null`) always pass.
   */
  const handleLineItemQuantityChange = (newQuantity) => {
    if (newQuantity > 0 && inventoryLimit !== null && newQuantity > inventoryLimit) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const applyQuantityChange = async (newQuantity) => {
    if (itemLoading) return
    try {
      handleLineItemQuantityChange(newQuantity)
    } catch (err) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: err.message
      })
      return
    }
    if (newQuantity > 0) setOptimisticQuantity(newQuantity)
    setItemLoading(true)
    try {
      await cartApi.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )
      handleSetCartConfig(CartConfigKeys.cart, await cartApi.getCart())
    } catch (error) {
      setOptimisticQuantity(null)
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
    } finally {
      setItemLoading(false)
    }
  }

  /**
   * Changes the variant of a line item by adding the new variant with the same
   * quantity and removing the old line (mirrors cart-line-variant-selector.ts).
   */
  const applyVariantChange = async (newVariantId) => {
    if (itemLoading) return
    if (!newVariantId || newVariantId === String(item.variant_id)) return
    const previousQuantity = item.quantity
    setItemLoading(true)
    try {
      await cartApi.swapLineItemVariant(item.key, parseInt(newVariantId, 10), previousQuantity)
      const updatedCart = await cartApi.getCart()
      handleSetCartConfig(CartConfigKeys.cart, updatedCart)
      Cart.dispatchRenderCartPage()

      const newLine = updatedCart?.items?.find(
        (line) => String(line.variant_id) === String(newVariantId)
      )
      if (newLine && newLine.quantity < previousQuantity) {
        globalEmitter.emit(emitterProps.notice.global, {
          type: 'error',
          content: 'Not enough stock of this variant. Quantity adjusted.'
        })
      }
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error instanceof Error ? error.message : 'Could not update variant'
      })
    } finally {
      setItemLoading(false)
    }
  }

  const onLineItemQuantityChange = async (e) => {
    e.preventDefault()
    const action = e.target.dataset.action
    const newQuantity = onChangeActionValue(action, e.target)
    await applyQuantityChange(newQuantity)
  }

  const onVariantChange = async (e) => {
    e.preventDefault()
    await applyVariantChange(e.target.value)
  }

  const onRemoveClick = async (e) => {
    e.preventDefault()
    await applyQuantityChange(0)
  }

  return (
    <div className="grow flex flex-col justify-end">
      <div className="flex items-center justify-between gap-x-0.5">
        <div className="flex items-end gap-x-1">
          {hasVariantSelector && (
            <form onChange={onVariantChange} className="shrink-0 flex flex-col">
              <label className="relative flex items-center gap-x-0.5 w-fit h-[44px] py-0.25 cursor-pointer link-fade">
                <span className="flex items-center gap-x-0.5 paragraph-xs leading-none whitespace-nowrap pointer-events-none">
                  <span>{variantLabel}{currentVariantOptionLabel}</span>
                  <span className="w-[8px] h-[4px] children:size-full">
                    <Icon id="chevron-down" />
                  </span>
                </span>
                <select
                  value={item.variant_id}
                  className="absolute inset-0 size-full opacity-0 border-0 bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-0 focus:ring-offset-0 z-2"
                  aria-label="Change item variant"
                >
                  {variants.map((variant) => (
                    <option value={variant.id} key={variant.id} disabled={variant.available === false}>
                      {getVariantOptionLabel(variant)}
                      {variant.available === false ? ' - Out of Stock' : ''}
                    </option>
                  ))}
                </select>
              </label>
            </form>
          )}

          <form onChange={onLineItemQuantityChange} className="shrink-0 flex flex-col">
            <label className="relative flex items-center w-fit h-[44px] py-0.25 cursor-pointer link-fade">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 paragraph-xs whitespace-nowrap">Qty: </span>
              <select
                data-action={actionKeys.SELECT}
                value={optimisticQuantity ?? item.quantity}
                className="paragraph-xs leading-none h-full pl-1.625 pr-0.75 py-0 border-0 bg-none bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-0 focus:ring-offset-0"
                aria-label="Quantity"
              >
                {quantityOptions.map((qty) => (
                  <option value={qty} key={qty}>{qty}</option>
                ))}
              </select>
              <span className="absolute right-0 top-1/2 -translate-y-1/2 w-[8px] h-[4px] pointer-events-none children:size-full">
                <Icon id="chevron-down" />
              </span>
            </label>
          </form>
        </div>

        <div className="">
          <a
            href={item.url_to_remove || '#'}
            className="btn btn-text btn-tertiary btn-s w-fit"
            aria-label="Remove item"
            onClick={onRemoveClick}
          >
            <span className="md-max:hidden text-line text-line-large">
              Remove
            </span>
            <div className="md:hidden w-[12px] h-[11px] children:size-full">
              <Icon id="trash" />
            </div>
          </a>
        </div>
      </div>

      <p
        className={clsx('empty:hidden w-fit max-h-0 pt-0.5 opacity-0 paragraph-xs text-type-error transition-all', error && '!max-h-none !opacity-100')}
        data-quantity-error
      >
        {error}
      </p>
    </div>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message = reachedGoal <= 0 ? (
    <>
      <div className="shrink-0 flex w-[12px] h-[9px] children:size-full">
        <Icon id={'tick'} />
      </div>
      <p className="paragraph-xs font-bold">You've got free shipping!</p>
    </>
  ) : (
    <>
      <p className="paragraph-xs">Spend <span className="font-bold">{formatMoney(reachedGoal)}</span> for free shipping</p>
    </>
  )
  const progressBarPercentage = Math.round((cartSubTotal / freeShippingGoal) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div
      data-role="free_shipping_monitor"
      className={`flex flex-col justify-between gap-0.5 h-auto px-1 md:px-1.5 py-1 transition-colors ${
        reachedGoal <= 0 ? 'bg-page-success' : 'bg-page-secondary'
      }`}
    >
      <div className="flex items-center gap-x-0.375">
        {message}
      </div>
      <div className="relative w-full h-[6px] bg-brand-quinary rounded-80 overflow-hidden">
        <div
          className="absolute inset-tl w-shipping-bar-width h-full bg-brand-primary transition-all"
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
    </div>
  )
}

/** Flattens a Shopify rich-text metafield value (or plain string) to text. */
const extractTextContent = (node) => {
  if (!node) return ''
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(extractTextContent).join('')
  if (typeof node === 'object') {
    if (typeof node.value === 'string') return node.value
    if (Array.isArray(node.children)) return node.children.map(extractTextContent).join('')
  }
  return ''
}

/**
 *
 * Spend & Save monitor. Mirrors the free shipping bar, driven by the
 * `spend_and_save` metaobject tiers (sorted ascending by min_threshold).
 * Below the next tier it shows that tier's message (with `[money]` replaced by
 * the amount still required); above the highest tier it shows the success_message.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSpendAndSave({ cartTotal, tiers = [] }) {
  const currentCountry = window.globals?.localization?.country
  const filtered = currentCountry
    ? tiers.filter(
      (t) =>
        !t.available_countries?.length ||
          t.available_countries.some((c) => c.country_code === currentCountry)
    )
    : tiers
  const sorted = [...filtered].sort((a, b) => a.min_threshold - b.min_threshold)

  if (!sorted.length) return null

  const total = cartTotal || 0
  const nextTier = sorted.find((t) => total < t.min_threshold)
  const isSuccess = !nextTier

  let messageHtml = ''
  let percentage = 100
  let textColour
  let bgColour
  let defaultTextClass = ''
  let defaultBgClass = 'bg-page-secondary'

  if (nextTier) {
    const remaining = nextTier.min_threshold - total
    const money = `<span class="font-bold">${formatMoney(remaining)}</span>`
    messageHtml = extractTextContent(nextTier.message).replace(/\[money\]/g, money)
    percentage = Math.min(100, Math.round((total / nextTier.min_threshold) * 100))
    textColour = nextTier.text_colour
    bgColour = nextTier.background_colour
  } else {
    const currentTier = sorted[sorted.length - 1]
    messageHtml = extractTextContent(currentTier.success_message)
    percentage = 100
    textColour = currentTier.success_text_colour
    bgColour = currentTier.success_background_colour
    defaultTextClass = 'text-type-black-primary'
    defaultBgClass = 'bg-page-success'
  }

  if (!messageHtml) return null

  const textClass = textColour ? 'text-[--ss-text-colour]' : defaultTextClass
  const bgClass = bgColour ? 'bg-[--ss-bg-colour]' : defaultBgClass

  return (
    <div
      data-role="spend_and_save_monitor"
      className={`flex flex-col justify-between gap-0.5 h-auto px-1 md:px-1.5 py-1 transition-colors ${textClass} ${bgClass}`}
      style={{ '--ss-text-colour': textColour || '', '--ss-bg-colour': bgColour || '' }}
    >
      <div className="flex items-center gap-x-0.375">
        {isSuccess && (
          <div className="shrink-0 flex w-[12px] h-[9px] children:size-full">
            <Icon id="tick" />
          </div>
        )}
        <p className={`paragraph-xs ${isSuccess ? 'font-bold' : ''}`} dangerouslySetInnerHTML={{ __html: messageHtml }} />
      </div>
      <div className="relative w-full h-[6px] bg-brand-quinary rounded-80 overflow-hidden">
        <div
          className="absolute inset-tl w-shipping-bar-width h-full bg-brand-primary transition-all"
          style={{ '--shipping-bar-width': percentage + '%' }}
        ></div>
      </div>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div className="flex flex-col items-end text-right">
        <span className="paragraph-xs font-bold text-brand-sale">{formatMoney(salePrice)}</span>
        <span className="paragraph-xs line-through">{formatMoney(originalPrice)}</span>
      </div>
    )
  }

  // Priority order: Discounts -> Compare At -> Regular
  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.compare_at_price * item.quantity} />
  }

  return (
    <div className="flex flex-col items-end text-right">
      <span className="paragraph-xs font-bold">{formatMoney(item.original_line_price)}</span>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts && item.discounts.length > 0 && (
        <ul className="list-none">
          {item.discounts.map((discount) => {
            return (
              <li className="flex gap-x-0.25 items-center paragraph-xs text-brand-sale list-none">
                <div className="size-[14px] children:size-full">
                  <Icon id={'tag'} />
                </div>
                {discount.title}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} className="shrink-0 link-fade" aria-label="Item image">
      <PreactImage
        className="flex w-[90px] md:w-[120px] aspect-1/1 children:size-full children:object-cover children:object-bottom"
        key={id}
        imageUrl={[imageSrc]}
        imageSrcSet={['120', '240', '360']}
        sizesAttr="(min-width: 768px) 120px, 90px"
      />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0
  const propertyEntries = hasProperties
    ? Object.entries(properties).filter(([key, value]) => value && !key.startsWith('_'))
    : []

  const criteria = useMemo(() => ({
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle],
    location: 'Minicart'
  }), [item.tags, item.collections, item.handle])

  const allPromolines = usePromolines(criteria)

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  const productName = item.mfield_utopia_customer_facing_product_name?.trim() ? item.mfield_utopia_customer_facing_product_name : item.name

  return (
    <div className="flex flex-row justify-between gap-x-1">
      <div className="flex flex-col gap-y-0.5">
        <div className="flex flex-col gap-y-0.25">
          <a href={item.url} className="paragraph-s font-bold link-fade" aria-label={productName}>
            {productName}
          </a>

          {item.mfield_utopia_colour_name && (
            <p className="paragraph-xs text-type-black-secondary">
              Colour: {item.mfield_utopia_colour_name}
            </p>
          )}
        </div>

        {propertyEntries.length > 0 && (
          <ul className="list-none">
            {propertyEntries.map(([key, value]) => (
              <li className="list-none" key={key}>
                <span className="paragraph-xs text-type-black-secondary">{key}: {value}</span>
              </li>
            ))}
          </ul>
        )}

        {(firstPromoline || secondPromoline) && (
          <div className="flex flex-col gap-0.25">
            {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="small" />}
            {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="small" />}
          </div>
        )}

        <MiniCartItemDiscount item={item} />
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, gift, handleSetCartConfig, isLoading }) => {
  const [itemLoading, setItemLoading] = useState(false)
  const loadingState = (isLoading || itemLoading) && 'opacity-50 pointer-events-none'

  return (
    <div className={`flex flex-col gap-y-0.75 md:gap-y-1 pb-1 md:pb-1.5 last:pb-0 border-b last:border-b-0 border-borders-default transition-all ${loadingState}`}>
      <div className="flex flex-row gap-x-1">
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
        <div className="flex-grow flex flex-col justify-between gap-y-0.5 md:gap-y-1 size-full">
          <MiniCartItemDetails item={item} />
          <MiniCartItemControls
            item={item}
            handleSetCartConfig={handleSetCartConfig}
            itemLoading={itemLoading}
            setItemLoading={setItemLoading}
          />
        </div>
      </div>
      {gift && (
        <GwpGiftItem
          gift={gift}
          onAddToCart={({ loading, cart: newCart }) => {
            if (loading !== undefined) handleSetCartConfig(CartConfigKeys.isLoading, loading)
            if (newCart) handleSetCartConfig(CartConfigKeys.cart, newCart)
          }}
          isLoading={isLoading}
          blockSpacing={true}
        />
      )}
    </div>
  )
}

/**
 * Calculates total savings from compare_at_price discounts
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total compare_at_price savings in cents
 */
const calculateCompareAtSavings = (cartItems) => {
  let compareAtSavings = 0

  for (const item of cartItems) {
    const linePricePerItem = item.line_price / item.quantity
    const compareAtPrice = item.compare_at_price

    if (compareAtPrice && compareAtPrice > linePricePerItem) {
      const savingsPerItem = compareAtPrice - linePricePerItem
      const itemSavings = savingsPerItem * item.quantity
      compareAtSavings += itemSavings
    }
  }

  return compareAtSavings
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading, cart, discountText, totalText, shippingText, freeShippingMinimum }) => {
  const cartTotal = formatMoney(total)
  const compareAtSavings = calculateCompareAtSavings(cart.items)
  const totalDiscountAmount = totalCartDiscount + compareAtSavings
  const totalDiscount = formatMoney(totalDiscountAmount)

  const shippingThreshold = Number(freeShippingMinimum)
  const freeShippingReached =
    Number.isFinite(shippingThreshold) && shippingThreshold > 0 && cart.items_subtotal_price >= shippingThreshold

  return (
    <div className="flex flex-col gap-y-0.5">
      {totalDiscountAmount > 0 && (
        <div className="flex justify-between gap-x-1 text-brand-sale">
          <p className="paragraph-s">{discountText || 'Discounts'}</p>
          <p className={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>-{totalDiscount}</p>
        </div>
      )}
      <div className="flex justify-between gap-x-1">
        <p className="paragraph-s">Shipping</p>
        <p className={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>{freeShippingReached ? 'FREE' : (shippingText || 'Calculated at checkout')}</p>
      </div>
      <div className="flex justify-between gap-x-1">
        <p className="paragraph-s font-bold">{totalText || 'Total'}</p>
        <p className={`paragraph-s font-bold transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
      </div>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading, emptyLink, emptyLinkText }) => {
  return (
    <>
      <div className={`grow flex flex-col gap-y-1.5 items-center justify-center p-1 md:p-1.5 text-center transition-all ${isLoading ? 'opacity-0' : ''}`}>
        {emptyLinkText &&
          <a href={emptyLink} className={'btn btn-primary btn-fill btn-m w-full'} aria-label={emptyLinkText}><span>{emptyLinkText}</span></a>
        }
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div>
      <a href="/checkout" className="btn btn-primary btn-fill btn-m w-full" aria-label={checkoutButtonTitle}>
        <span>{checkoutButtonTitle}</span>
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, giftsWithoutThreshold, handleSetCartConfig, isLoading }) => {
  const giftsDisplayed = []
  return (
    <div className="grow flex flex-col gap-y-1 md:gap-y-1.5 px-1 md:px-1.5 py-1 md:py-1.5" data-role="cart_items">
      {cart.items.map((item) => {
        const giftToDisplay = giftsWithoutThreshold.find(gift => {
          if (!gift.cartQualifies || gift.giftInCart || giftsDisplayed.includes(gift.id)) {
            return false
          }
          switch (gift.conditionalType) {
            case 'all':
              return true
            case 'products':
              return gift.conditionalIds.some(id => id.split('/').pop() === item.product_id.toString())
            case 'collections':
              return item.collections?.some(collection => gift.conditionalIds.some(id => id.split('/').pop() === collection.id.toString()))
            case 'tags':
              return item.tags?.some(tag => gift.conditionalTags.includes(tag))
            default:
              return false
          }
        })
        if (giftToDisplay) {
          giftsDisplayed.push(giftToDisplay.id)
        }
        return <MiniCartLineItem key={item.key} item={{ ...item }} gift={giftToDisplay} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum
  const shippingBlock = document.getElementById('minicart-shipping')
  const showShippingBar = shippingBlock?.dataset.showShippingBar !== 'false'
  const showSpendAndSave = shippingBlock?.dataset.showSpendAndSave !== 'false'
  const { giftsWithoutThreshold } = useGwp(cart, freeShippingMinimum)

  const { checkoutButtonTitle, emptyLink, emptyLinkText, discountText, totalText } = globalCart

  useLayoutEffect(() => {
    document.dispatchEvent(
      new CustomEvent('minicart:cart-rendered', {
        detail: { itemCount: cart?.item_count ?? 0 }
      })
    )
  }, [cart?.item_count])

  useEffect(() => {
    const contentEl = document.getElementById('minicart-content')
    if (contentEl) {
      contentEl.classList.toggle('opacity-50', isLoading)
      contentEl.classList.toggle('pointer-events-none', isLoading)
    }
  }, [isLoading])

  const hasCartItems = cart?.items?.length > 0

  useEffect(() => {
    const summaryWrapper = document.getElementById('minicart-summary')
    const checkoutButtons = document.querySelectorAll(
      'div.additional-checkout-buttons--vertical, div.additional-checkout-buttons--horizontal'
    )

    if (summaryWrapper) {
      summaryWrapper.classList.toggle('hidden', !hasCartItems)
    }

    checkoutButtons.forEach((buttonGroup) => {
      buttonGroup.classList.toggle('hidden', !hasCartItems)
    })
  }, [hasCartItems])

  useEffect(() => {
    const handleRenderMiniCart = () => {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      cartApi.getCart().then((updatedCart) => {
        handleSetCartConfig(CartConfigKeys.cart, updatedCart)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }

    document.addEventListener('renderMiniCart', handleRenderMiniCart)
    return () => document.removeEventListener('renderMiniCart', handleRenderMiniCart)
  }, [])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <div className="flex items-stretch w-full border-b border-borders-default children:grow children:w-0 children:border-r children:border-borders-default children-last:border-r-0">
              {showShippingBar && <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />}
              {showSpendAndSave && <MiniCartSpendAndSave cartTotal={cart.total_price} tiers={globalState.metaobjects?.spend_and_save} />}
            </div>
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-gwp">
            <GwpCartBlock
              cart={cart}
              freeShippingMinimum={freeShippingMinimum}
              enableProgressBar={globalCart.gwp?.enableProgressBar}
              handleSetCartConfig={handleSetCartConfig}
              isLoading={isLoading}
              blockSpacing={true}
            />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} giftsWithoutThreshold={giftsWithoutThreshold} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div className="flex flex-col gap-y-1">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                discountText={discountText}
                totalText={totalText}
                freeShippingMinimum={freeShippingMinimum}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} emptyLink={emptyLink} emptyLinkText={emptyLinkText} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
